import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useAuth } from "../../../context";
import useFetchObjects from "../../../api/useFetchObjects";
import { useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ReportPdfLayout from "../../../Components/ReportPdfLayout";

const formatCurrency = (val) => {
  if (val == null || val === undefined) return "—";
  const n = Number(val);
  if (isNaN(n)) return "—";
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const SERVICE_KEYS = [
  "appointments",
  "prescriptions",
  "medicine_orders",
  "ultrasounds",
  "xrays",
  "echos",
  "laboratory_tests",
  "dental_payments",
  "surgeries",
  "medicine_returns",
  "emergency_payments",
  "ipd",
  "icu",
  "endoscopy",
  "ct_scan",
  "m_roy",
];

const ReceptionRevenueReport = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const auth = useAuth();
  const token = auth?.user?.token;

  const [reportType, setReportType] = useState("monthly");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [expensesOpen, setExpensesOpen] = useState(false);

  const params = useMemo(() => {
    const p = new URLSearchParams();
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      p.append("start_datetime", start.toISOString().slice(0, 19));
      p.append("end_datetime", end.toISOString().slice(0, 19));
    } else {
      p.append("type", reportType);
    }
    return p.toString();
  }, [reportType, startDate, endDate]);

  const endpoint = `reception-reports/?${params}`;
  const { data, isLoading, isError, error, refetch } = useFetchObjects(
    ["reception-revenue", params],
    endpoint,
    token
  );

  const handleApply = () => refetch();

  const summary = data?.summary ?? {};
  const medicalServices = data?.medical_services ?? {};
  const expenses = data?.expenses ?? {};
  const dateRange = data?.date_range ?? {};

  return (
    <Grid container maxWidth="xl" sx={{ p: 3, width: "100%" }}>
      <Grid item xs={12}>
        <ReportPdfLayout
          reportTitle={t("reports.receptionRevenueReport")}
          fileName="reception-revenue-report.pdf"
          disabled={isLoading || isError || !data}
        >
          <Grid container>
            <Grid item xs={12} className="no-print">
              <Typography variant="h5" fontWeight={600} gutterBottom>
                {t("reports.receptionRevenueReport")}
              </Typography>
            </Grid>
            <Grid item xs={12} mb={2} className="no-print">
              <Paper sx={{ p: 2, display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>{t("reports.reportType")}</InputLabel>
            <Select
              value={reportType}
              label={t("reports.reportType")}
              onChange={(e) => setReportType(e.target.value)}
            >
              <MenuItem value="daily">{t("reports.daily")}</MenuItem>
              <MenuItem value="weekly">{t("reports.weekly")}</MenuItem>
              <MenuItem value="monthly">{t("reports.monthly")}</MenuItem>
            </Select>
          </FormControl>
          <DatePicker
            selected={startDate}
            onChange={(d) => setStartDate(d)}
            placeholderText={t("reports.startDate")}
            customInput={<TextField size="small" sx={{ minWidth: 160 }} />}
          />
          <DatePicker
            selected={endDate}
            onChange={(d) => setEndDate(d)}
            placeholderText={t("reports.endDate")}
            customInput={<TextField size="small" sx={{ minWidth: 160 }} />}
          />
                <Button variant="contained" onClick={handleApply}>
                  {t("reports.apply")}
                </Button>
              </Paper>
            </Grid>
            {isError && (
              <Grid item xs={12}>
                <Alert severity="error">{error?.message || t("reports.errorLoading")}</Alert>
              </Grid>
            )}
            {isLoading && (
              <Grid item xs={12} display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Grid>
            )}
            {!isLoading && !isError && data && (
        <>
          {dateRange.start && (
            <Grid item xs={12} mb={2}>
              <Typography variant="body1">
                {t("reports.dateRange")}: {dateRange.start} — {dateRange.end}
              </Typography>
            </Grid>
          )}

          <Grid item xs={12} container spacing={2} mb={3}>
            <Grid item xs={12} sm={4}>
              <Paper sx={{ p: 2, borderLeft: `4px solid ${theme.palette.primary.main}` }}>
                <Typography variant="body2" color="text.secondary">
                  {t("reports.totalRevenue")}
                </Typography>
                <Typography variant="h6" fontWeight={600}>
                  {formatCurrency(summary.total_revenue)}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper sx={{ p: 2, borderLeft: "4px solid", borderColor: "error.main" }}>
                <Typography variant="body2" color="text.secondary">
                  {t("reports.totalExpenses")}
                </Typography>
                <Typography variant="h6" fontWeight={600}>
                  {formatCurrency(summary.total_expenses)}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper sx={{ p: 2, borderLeft: "4px solid", borderColor: "success.main" }}>
                <Typography variant="body2" color="text.secondary">
                  {t("reports.netIncome")}
                </Typography>
                <Typography variant="h6" fontWeight={600}>
                  {formatCurrency(summary.net_income)}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          <Grid item xs={12} mb={2}>
            <Typography variant="h6" gutterBottom>
              {t("reports.medicalServices")}
            </Typography>
            <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: "divider" }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: theme.palette.primary.main }}>
                    <TableCell sx={{ color: "white", fontWeight: 600 }}>{t("reports.type")}</TableCell>
                    <TableCell sx={{ color: "white", fontWeight: 600 }} align="right">
                      {t("reports.patientCount")}
                    </TableCell>
                    <TableCell sx={{ color: "white", fontWeight: 600 }} align="right">
                      {t("reports.totalAmount")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {SERVICE_KEYS.map((key) => {
                    const section =
                      key === "endoscopy"
                        ? medicalServices?.endoscopy_payments ?? medicalServices?.endoscopy
                        : key === "ct_scan"
                          ? medicalServices?.ct_scan_payments ?? medicalServices?.ct_scan
                          : key === "m_roy"
                            ? medicalServices?.m_roy_payments ?? medicalServices?.m_roy
                        : medicalServices[key];
                    if (!section) return null;
                    const label =
                      key === "endoscopy" ? "Endoscopy" : key === "ct_scan" ? "CT Scan" : key === "m_roy" ? "MRI" : key.replace(/_/g, " ");
                    return (
                      <TableRow key={key}>
                        <TableCell sx={{ textTransform: "capitalize" }}>{label}</TableCell>
                        <TableCell align="right">{section.count ?? 0}</TableCell>
                        <TableCell align="right">{formatCurrency(section.total)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          <Grid item xs={12}>
            <Button
              startIcon={expensesOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              onClick={() => setExpensesOpen(!expensesOpen)}
            >
              {t("reports.expenses")}
            </Button>
            <Collapse in={expensesOpen}>
              <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: "divider", mt: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: theme.palette.grey[300] }}>
                      <TableCell sx={{ fontWeight: 600 }}>{t("reports.type")}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {t("reports.patientCount")}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {t("reports.totalAmount")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {["purchase_payments", "expenses", "payroll", "advances", "bonuses"].map((key) => {
                      const section = expenses[key];
                      if (!section) return null;
                      const label = key.replace(/_/g, " ");
                      return (
                        <TableRow key={key}>
                          <TableCell sx={{ textTransform: "capitalize" }}>{label}</TableCell>
                          <TableCell align="right">{section.count ?? 0}</TableCell>
                          <TableCell align="right">{formatCurrency(section.total)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Collapse>
          </Grid>
        </>
            )}
          </Grid>
        </ReportPdfLayout>
      </Grid>
    </Grid>
  );
};

export default ReceptionRevenueReport;
